// src/components/admin/LOCContentUploader.tsx
// DROP 18f — LOC (Local Organizing Committee) Content Upload System
// Platform Admin or authorized Content Uploader only

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Upload, FileText, Image, Video, Music, File,
  CheckCircle, AlertTriangle, RefreshCw, Trash2,
  ChevronDown, Search, Eye, Download, Globe,
  ToggleLeft, ToggleRight, Calendar, User, Plus, X
} from "lucide-react";

interface Community {
  id: string;
  name: string;
  displayName: string;
  icon: string;
  type: string;
}

interface ContentItem {
  id: string;
  communityId: string;
  type: "text"|"image"|"video"|"audio"|"document";
  title: string;
  content?: string;
  url?: string;
  filename?: string;
  fileSize?: number;
  mimeType?: string;
  status: "draft"|"published"|"archived";
  uploadedBy: string;
  uploadedAt: string;
  publishedAt?: string;
  scheduledFor?: string;
  category: string;
  description?: string;
}

const CONTENT_CATEGORIES = [
  "History & Heritage",
  "Festival Participation",
  "Leadership & LOC",
  "Community News",
  "Cultural Activities",
  "Sports & Recreation",
  "Youth Programs",
  "Women Affairs",
  "Announcements",
  "Gallery",
  "Documents",
  "Other",
];

const TYPE_ICONS: Record<string, React.ReactNode> = {
  text:     <FileText size={16}/>,
  image:    <Image size={16}/>,
  video:    <Video size={16}/>,
  audio:    <Music size={16}/>,
  document: <File size={16}/>,
};

const TYPE_COLORS: Record<string, string> = {
  text:     "#60A5FA",
  image:    "#4ADE80",
  video:    "#F472B6",
  audio:    "#A78BFA",
  document: "#FDE047",
};

const ACCEPT_MAP: Record<string, string> = {
  image:    "image/jpeg,image/png,image/webp,image/gif",
  video:    "video/mp4,video/webm,video/mov",
  audio:    "audio/mpeg,audio/mp3,audio/wav,audio/ogg",
  document: "application/pdf,.doc,.docx",
};

export default function LOCContentUploader() {
  const [communities, setCommunities]   = useState<Community[]>([]);
  const [selectedCom, setSelectedCom]   = useState<Community|null>(null);
  const [contents,    setContents]      = useState<ContentItem[]>([]);
  const [search,      setSearch]        = useState("");
  const [filterType,  setFilterType]    = useState<string>("all");
  const [filterStatus,setFilterStatus]  = useState<string>("all");
  const [loading,     setLoading]       = useState(false);
  const [uploading,   setUploading]     = useState(false);
  const [msg,         setMsg]           = useState<{type:"ok"|"err";text:string}|null>(null);
  const [showForm,    setShowForm]      = useState(false);

  // Form state
  const [fType,       setFType]         = useState<ContentItem["type"]>("text");
  const [fTitle,      setFTitle]        = useState("");
  const [fContent,    setFContent]      = useState("");
  const [fCategory,   setFCategory]     = useState(CONTENT_CATEGORIES[0]);
  const [fDesc,       setFDesc]         = useState("");
  const [fStatus,     setFStatus]       = useState<"draft"|"published">("draft");
  const [fSchedule,   setFSchedule]     = useState("");
  const [fUrl,        setFUrl]          = useState("");
  const [fFile,       setFFile]         = useState<File|null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const token = () => localStorage.getItem("erue_auth_token") || "";
  const flash  = (type:"ok"|"err", text:string) => {
    setMsg({type,text}); setTimeout(()=>setMsg(null),5000);
  };
  const api = useCallback(async (url:string, opts:RequestInit={}) => {
    const res = await fetch(url,{...opts,headers:{"Content-Type":"application/json",Authorization:`Bearer ${token()}`, ...(opts.headers||{})}});
    return res.json();
  },[]);

  const loadCommunities = useCallback(async () => {
    const d = await api("/api/admin/communities");
    if (d.success) setCommunities(d.communities||[]);
  },[api]);

  const loadContents = useCallback(async (comId: string) => {
    setLoading(true);
    try {
      const d = await api(`/api/content/${comId}`);
      if (d.success) setContents(d.items||[]);
    } finally { setLoading(false); }
  },[api]);

  useEffect(()=>{ loadCommunities(); },[loadCommunities]);
  useEffect(()=>{ if(selectedCom) loadContents(selectedCom.id); },[selectedCom,loadContents]);

  const resetForm = () => {
    setFType("text"); setFTitle(""); setFContent(""); setFCategory(CONTENT_CATEGORIES[0]);
    setFDesc(""); setFStatus("draft"); setFSchedule(""); setFUrl(""); setFFile(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setFFile(f);
  };

  const uploadFile = async (): Promise<string|null> => {
    if (!fFile) return fUrl || null;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", fFile);
      fd.append("folder", `community-content/${selectedCom?.id||"general"}`);
      const res = await fetch("/api/community-portals/upload",{
        method:"POST",
        headers:{ Authorization:`Bearer ${token()}` },
        body: fd
      });
      const d = await res.json();
      if (d.success && d.url) return d.url;
      flash("err","File upload failed"); return null;
    } catch { flash("err","Upload error"); return null; }
    finally { setUploading(false); }
  };

  const saveContent = async () => {
    if (!selectedCom) { flash("err","Select a community first"); return; }
    if (!fTitle.trim()) { flash("err","Title is required"); return; }
    if (fType === "text" && !fContent.trim()) { flash("err","Content text is required"); return; }

    let fileUrl = fUrl;
    if (fFile) {
      const uploaded = await uploadFile();
      if (!uploaded) return;
      fileUrl = uploaded;
    }

    const payload = {
      communityId: selectedCom.id,
      type: fType, title: fTitle.trim(),
      content: fContent, url: fileUrl,
      filename: fFile?.name, fileSize: fFile?.size,
      mimeType: fFile?.type, category: fCategory,
      description: fDesc, status: fStatus,
      scheduledFor: fSchedule || null,
    };

    const d = await api("/api/content", { method:"POST", body:JSON.stringify(payload) });
    if (d.success) {
      flash("ok",`Content ${fStatus === "published" ? "published" : "saved as draft"} successfully`);
      setShowForm(false); resetForm(); loadContents(selectedCom.id);
    } else flash("err", d.error||"Failed to save");
  };

  const toggleStatus = async (item: ContentItem) => {
    const newStatus = item.status === "published" ? "draft" : "published";
    const d = await api(`/api/content/${item.id}`,{
      method:"PUT", body:JSON.stringify({ status:newStatus })
    });
    if (d.success) { flash("ok",`${newStatus==="published"?"Published":"Unpublished"} successfully`); loadContents(selectedCom!.id); }
    else flash("err", d.error||"Failed");
  };

  const archiveContent = async (id: string) => {
    if (!confirm("Archive this content?")) return;
    const d = await api(`/api/content/${id}`,{ method:"PUT", body:JSON.stringify({status:"archived"}) });
    if (d.success) { flash("ok","Archived"); loadContents(selectedCom!.id); }
    else flash("err", d.error||"Failed");
  };

  const deleteContent = async (id: string) => {
    if (!confirm("Permanently delete this content?")) return;
    const d = await api(`/api/content/${id}`,{ method:"DELETE" });
    if (d.success) { flash("ok","Deleted"); loadContents(selectedCom!.id); }
    else flash("err", d.error||"Failed");
  };

  const filteredContents = contents
    .filter(c => filterType==="all" || c.type===filterType)
    .filter(c => filterStatus==="all" || c.status===filterStatus)
    .filter(c => !search || c.title.toLowerCase().includes(search.toLowerCase()) ||
                            c.category.toLowerCase().includes(search.toLowerCase()));

  const fmtBytes = (b?: number) => {
    if (!b) return "";
    if (b < 1024) return `${b}B`;
    if (b < 1048576) return `${(b/1024).toFixed(1)}KB`;
    return `${(b/1048576).toFixed(1)}MB`;
  };

  return (
    <div className="flex h-full gap-4">
      {/* ── LEFT: Community selector ────────────────────────────── */}
      <div className="w-64 flex-shrink-0 space-y-1 overflow-y-auto pr-2"
           style={{ maxHeight:"calc(100vh - 200px)" }}>
        <p className="text-xs uppercase tracking-wider mb-3 px-2"
           style={{ color:"rgba(237,232,220,0.4)" }}>Select Community</p>

        {/* Filter: all / village / special */}
        <div className="flex gap-1 mb-3">
          {["All","Villages","Special"].map((f,i) => (
            <button key={f} className="flex-1 py-1 rounded text-xs transition-all"
                    style={{ background:"rgba(14,24,18,0.8)", color:"rgba(237,232,220,0.5)", border:"1px solid #1C2E22" }}
                    onClick={e=>{
                      const btns = (e.currentTarget.parentNode as HTMLElement).querySelectorAll("button");
                      btns.forEach(b=>{ (b as HTMLElement).style.background="rgba(14,24,18,0.8)"; (b as HTMLElement).style.color="rgba(237,232,220,0.5)"; });
                      (e.currentTarget as HTMLElement).style.background="rgba(201,168,76,0.15)";
                      (e.currentTarget as HTMLElement).style.color="#C9A84C";
                    }}>
              {f}
            </button>
          ))}
        </div>

        {communities.map(c => (
          <button key={c.id} onClick={()=>setSelectedCom(c)}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all"
                  style={{
                    background: selectedCom?.id===c.id ? "rgba(201,168,76,0.12)" : "transparent",
                    border: `1px solid ${selectedCom?.id===c.id ? "rgba(201,168,76,0.25)" : "transparent"}`,
                  }}
                  onMouseEnter={e=>{ if(selectedCom?.id!==c.id)(e.currentTarget as HTMLElement).style.background="rgba(14,24,18,0.6)"; }}
                  onMouseLeave={e=>{ if(selectedCom?.id!==c.id)(e.currentTarget as HTMLElement).style.background="transparent"; }}>
            <span className="text-lg flex-shrink-0">{c.icon}</span>
            <div className="min-w-0">
              <p className="text-xs font-medium truncate" style={{ color:selectedCom?.id===c.id?"#C9A84C":"#EDE8DC" }}>
                {c.displayName||c.name}
              </p>
              <p className="text-xs" style={{ color:"rgba(237,232,220,0.3)", fontSize:"10px" }}>
                {contents.filter(x=>x.communityId===c.id).length} items
              </p>
            </div>
          </button>
        ))}
      </div>

      {/* ── RIGHT: Content manager ───────────────────────────────── */}
      <div className="flex-1 min-w-0">
        {!selectedCom ? (
          <div className="flex flex-col items-center justify-center h-64"
               style={{ color:"rgba(237,232,220,0.3)" }}>
            <Globe size={40} className="mb-3 opacity-30"/>
            <p className="text-sm">Select a community to manage its content</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{selectedCom.icon}</span>
                <div>
                  <h2 className="text-base font-semibold" style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
                    {selectedCom.displayName||selectedCom.name}
                  </h2>
                  <p className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>
                    {filteredContents.length} items · Click ➕ to add content
                  </p>
                </div>
              </div>
              <button onClick={()=>{ resetForm(); setShowForm(true); }}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold"
                      style={{ background:"#C9A84C", color:"#070D0A" }}>
                <Plus size={13}/> Add Content
              </button>
            </div>

            {/* Flash */}
            {msg && (
              <div className="mb-3 px-4 py-3 rounded-xl text-sm flex items-center gap-2"
                   style={{ background:msg.type==="ok"?"rgba(74,222,128,0.1)":"rgba(239,68,68,0.1)", border:`1px solid ${msg.type==="ok"?"rgba(74,222,128,0.3)":"rgba(239,68,68,0.3)"}`, color:msg.type==="ok"?"#4ADE80":"#FCA5A5" }}>
                {msg.type==="ok"?<CheckCircle size={14}/>:<AlertTriangle size={14}/>} {msg.text}
              </div>
            )}

            {/* Filters */}
            <div className="flex gap-2 mb-4 flex-wrap">
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color:"rgba(237,232,220,0.3)" }}/>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…"
                       className="pl-7 pr-3 py-1.5 rounded-lg text-xs outline-none"
                       style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC", width:"160px" }}/>
              </div>
              <select value={filterType} onChange={e=>setFilterType(e.target.value)}
                      className="px-2 py-1.5 rounded-lg text-xs outline-none"
                      style={{ background:"#121E16", border:"1px solid #1C2E22", color:"rgba(237,232,220,0.6)" }}>
                <option value="all">All types</option>
                <option value="text">Text</option>
                <option value="image">Images</option>
                <option value="video">Videos</option>
                <option value="audio">Audio</option>
                <option value="document">Documents</option>
              </select>
              <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
                      className="px-2 py-1.5 rounded-lg text-xs outline-none"
                      style={{ background:"#121E16", border:"1px solid #1C2E22", color:"rgba(237,232,220,0.6)" }}>
                <option value="all">All status</option>
                <option value="published">Published</option>
                <option value="draft">Drafts</option>
                <option value="archived">Archived</option>
              </select>
              <button onClick={()=>loadContents(selectedCom.id)}
                      className="p-1.5 rounded-lg border transition-all"
                      style={{ borderColor:"#1C2E22", color:"rgba(237,232,220,0.4)" }}>
                <RefreshCw size={13} className={loading?"animate-spin":""}/>
              </button>
            </div>

            {/* Content list */}
            {loading ? (
              <div className="text-center py-12" style={{ color:"rgba(237,232,220,0.3)" }}>
                <RefreshCw size={24} className="animate-spin mx-auto mb-2"/>Loading…
              </div>
            ) : filteredContents.length === 0 ? (
              <div className="text-center py-12" style={{ color:"rgba(237,232,220,0.3)" }}>
                <Upload size={32} className="mx-auto mb-2 opacity-30"/>
                <p className="text-sm">No content yet</p>
                <p className="text-xs mt-1">Click "+ Add Content" to upload the first item</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredContents.map(item => (
                  <div key={item.id} className="flex items-center gap-3 p-3 rounded-xl"
                       style={{ background:"#121E16", border:"1px solid #1C2E22" }}>
                    {/* Type icon */}
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                         style={{ background:`${TYPE_COLORS[item.type]}15`, color:TYPE_COLORS[item.type] }}>
                      {TYPE_ICONS[item.type]}
                    </div>

                    {/* Content info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate" style={{ color:"#EDE8DC" }}>{item.title}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded flex-shrink-0"
                              style={{ background:`${item.status==="published"?"rgba(74,222,128":"rgba(250,204,21"},0.1)`, color:item.status==="published"?"#4ADE80":"#FDE047", fontSize:"10px" }}>
                          {item.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>{item.category}</span>
                        {item.fileSize && <span className="text-xs" style={{ color:"rgba(237,232,220,0.3)" }}>· {fmtBytes(item.fileSize)}</span>}
                        <span className="text-xs" style={{ color:"rgba(237,232,220,0.3)" }}>· {new Date(item.uploadedAt).toLocaleDateString()}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {item.url && (
                        <a href={item.url} target="_blank" rel="noopener noreferrer"
                           className="p-1.5 rounded-lg transition-all"
                           style={{ color:"rgba(237,232,220,0.4)" }}
                           onMouseEnter={e=>(e.currentTarget as HTMLElement).style.color="#C9A84C"}
                           onMouseLeave={e=>(e.currentTarget as HTMLElement).style.color="rgba(237,232,220,0.4)"}>
                          <Eye size={13}/>
                        </a>
                      )}
                      <button onClick={()=>toggleStatus(item)}
                              className="p-1.5 rounded-lg transition-all"
                              style={{ color: item.status==="published"?"#4ADE80":"rgba(237,232,220,0.3)" }}>
                        {item.status==="published" ? <ToggleRight size={17}/> : <ToggleLeft size={17}/>}
                      </button>
                      <button onClick={()=>archiveContent(item.id)}
                              className="p-1.5 rounded-lg transition-all"
                              style={{ color:"rgba(237,232,220,0.3)" }}
                              onMouseEnter={e=>(e.currentTarget as HTMLElement).style.color="#FDE047"}
                              onMouseLeave={e=>(e.currentTarget as HTMLElement).style.color="rgba(237,232,220,0.3)"}>
                        <Download size={13}/>
                      </button>
                      <button onClick={()=>deleteContent(item.id)}
                              className="p-1.5 rounded-lg transition-all"
                              style={{ color:"rgba(237,232,220,0.3)" }}
                              onMouseEnter={e=>(e.currentTarget as HTMLElement).style.color="#FCA5A5"}
                              onMouseLeave={e=>(e.currentTarget as HTMLElement).style.color="rgba(237,232,220,0.3)"}>
                        <Trash2 size={13}/>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── ADD CONTENT MODAL ──────────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto">
          <div className="w-full max-w-xl bg-[#0E1812] border border-[#C9A84C]/25 rounded-2xl p-6 mt-8 mb-8">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold" style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
                Add Content — {selectedCom?.displayName||selectedCom?.name}
              </h3>
              <button onClick={()=>{ setShowForm(false); resetForm(); }}
                      style={{ color:"rgba(237,232,220,0.4)" }}><X size={18}/></button>
            </div>

            <div className="space-y-4">
              {/* Content type */}
              <div>
                <label className="text-xs uppercase tracking-wider mb-2 block" style={{ color:"rgba(237,232,220,0.4)" }}>
                  Content Type
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {(["text","image","video","audio","document"] as ContentItem["type"][]).map(t => (
                    <button key={t} onClick={()=>{ setFType(t); setFFile(null); setFUrl(""); }}
                            className="flex flex-col items-center gap-1.5 py-3 rounded-xl border text-xs transition-all capitalize"
                            style={{
                              background: fType===t ? `${TYPE_COLORS[t]}15` : "transparent",
                              borderColor: fType===t ? `${TYPE_COLORS[t]}50` : "#1C2E22",
                              color: fType===t ? TYPE_COLORS[t] : "rgba(237,232,220,0.4)",
                            }}>
                      <span style={{ color:TYPE_COLORS[t] }}>{TYPE_ICONS[t]}</span>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Title *</label>
                <input value={fTitle} onChange={e=>setFTitle(e.target.value)}
                       placeholder="Content title or headline"
                       className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                       style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
              </div>

              {/* Category */}
              <div>
                <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Category</label>
                <select value={fCategory} onChange={e=>setFCategory(e.target.value)}
                        className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                        style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}>
                  {CONTENT_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Text content */}
              {fType === "text" && (
                <div>
                  <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Content *</label>
                  <textarea value={fContent} onChange={e=>setFContent(e.target.value)} rows={6}
                            placeholder="Paste or type the content here. This can be history, news, announcements, etc."
                            className="w-full px-3 py-2.5 rounded-lg text-sm outline-none resize-none"
                            style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                </div>
              )}

              {/* File upload for non-text */}
              {fType !== "text" && (
                <div>
                  <label className="text-xs mb-2 block" style={{ color:"rgba(237,232,220,0.4)" }}>
                    Upload File or Enter URL
                  </label>
                  <div className="space-y-2">
                    {/* File picker */}
                    <input ref={fileRef} type="file"
                           accept={ACCEPT_MAP[fType]||"*/*"}
                           className="hidden"
                           onChange={handleFileChange}/>
                    <button onClick={()=>fileRef.current?.click()}
                            className="w-full flex items-center gap-2 px-4 py-3 rounded-xl border border-dashed text-sm transition-all"
                            style={{ borderColor:"rgba(201,168,76,0.25)", color:"rgba(237,232,220,0.5)" }}
                            onMouseEnter={e=>(e.currentTarget as HTMLElement).style.borderColor="rgba(201,168,76,0.4)"}
                            onMouseLeave={e=>(e.currentTarget as HTMLElement).style.borderColor="rgba(201,168,76,0.25)"}>
                      <Upload size={16} style={{ color:"#C9A84C" }}/>
                      {fFile ? (
                        <span style={{ color:"#4ADE80" }}>{fFile.name} ({(fFile.size/1024).toFixed(1)}KB)</span>
                      ) : (
                        <span>Click to select {fType} file</span>
                      )}
                    </button>

                    {/* OR URL */}
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs"
                            style={{ color:"rgba(237,232,220,0.3)" }}>URL:</span>
                      <input value={fUrl} onChange={e=>setFUrl(e.target.value)}
                             placeholder="Or paste Cloudinary/external URL"
                             className="w-full pl-12 pr-3 py-2.5 rounded-lg text-sm outline-none"
                             style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                    </div>
                  </div>
                </div>
              )}

              {/* Description */}
              <div>
                <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>
                  Description / Caption (optional)
                </label>
                <textarea value={fDesc} onChange={e=>setFDesc(e.target.value)} rows={2}
                          placeholder="Brief description or caption"
                          className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                          style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
              </div>

              {/* Status + Schedule */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Status</label>
                  <select value={fStatus} onChange={e=>setFStatus(e.target.value as "draft"|"published")}
                          className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                          style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}>
                    <option value="draft">Save as Draft</option>
                    <option value="published">Publish Immediately</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>
                    Schedule Publish (optional)
                  </label>
                  <input type="datetime-local" value={fSchedule} onChange={e=>setFSchedule(e.target.value)}
                         className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                         style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button onClick={()=>{ setShowForm(false); resetForm(); }}
                        className="flex-1 py-2.5 rounded-xl text-sm border"
                        style={{ borderColor:"#1C2E22", color:"rgba(237,232,220,0.5)" }}>
                  Cancel
                </button>
                <button onClick={saveContent} disabled={uploading}
                        className="flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                        style={{ background:uploading?"rgba(201,168,76,0.4)":"#C9A84C", color:"#070D0A" }}>
                  {uploading ? <><RefreshCw size={14} className="animate-spin"/>Uploading…</> :
                               <><Upload size={14}/>{fStatus==="published"?"Publish":"Save Draft"}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
